import axios from "axios";

/** A single planned aid-station stop. */
export interface AidStation {
  /** Cumulated course distance (km) at which the stop occurs */
  distance_km: number;
  /** Expected stop duration in seconds */
  idle_time_s: number;
}

// Allow override of API server via localStorage.SERVER
const API_BASE_URL = localStorage.getItem("SERVER") || import.meta.env.VITE_API_BASE_URL || "";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("user-token");
    if (token) {
      // Backend expects token directly without "Bearer " prefix
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("❌ API Response Error:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
      headers: error.response?.headers,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem("user-token");
      // Redirect to login page instead of unauthorized
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials: { username: string; password: string }) =>
    api.post("/api/login", credentials),
  signup: (userData: unknown, config?: any) => api.post("/api/signup", userData, config),
  register: (userData: {
    email: string;
    password: string;
    password_confirmation: string;
    firstname?: string;
    lastname?: string;
    enabled_features?: Record<string, boolean>;
  }) => api.post("/api/register", userData),
  confirmEmail: (token: string) => api.post("/api/confirm-email", { token }),
  requestPasswordReset: (email: string) => api.post("/api/password-reset/request", { email }),
  confirmPasswordReset: (token: string, password: string, password_confirmation: string) =>
    api.post("/api/password-reset/confirm", { token, password, password_confirmation }),
};

// User APIs
export const userAPI = {
  getProfile: () => api.get("/api/profile", { params: { with_profile: true } }),
  updateProfile: (profile: unknown) => api.post("/api/profile", profile),
  importActivities: () => api.post("/api/import-activities"),
};

// Activities APIs
export const activitiesAPI = {
  getActivities: (params: {
    page: number;
    sort: string;
    sport: string;
    sortOrder: number;
    isPublic?: boolean;
    race?: boolean;
  }) => {
    // Backend expects: page_number, page_length, sortby, sortorder, sport
    const backendParams: Record<string, string | number | boolean> = {
      page_number: params.page - 1, // Backend uses 0-based index
      page_length: 20,
      sortby: params.sort === "date" ? "start_time" : params.sort,
      sortorder: params.sortOrder,
    };
    if (params.sport && params.sport !== "all") {
      backendParams.sport = params.sport;
    }
    if (params.isPublic) {
      backendParams.is_public = true;
    }
    if (params.race !== undefined) {
      backendParams.race = params.race;
    }
    return api.get("/api/activities", { params: backendParams });
  },
  getActivity: (id: string) => api.get(`/api/activities/${id}`),
  getActivityGPX: (id: string) => api.get(`/api/activities/${id}/gpx`, { responseType: "blob" }),
  makePublic: (id: string) => api.post(`/api/activities/${id}/public`),
  setPrivacy: (id: string, privacy: "public" | "private") =>
    api.patch(`/api/activities/${id}/privacy`, { privacy }),
  updateActivity: (id: string, data: { name?: string; race?: boolean }) =>
    api.patch(`/api/activities/${id}`, data),
  uploadToStrava: (id: string, options: { description?: string } = {}) =>
    api.post(`/api/activities/${id}/upload-to-strava`, options),
  deleteActivity: (id: string) => api.delete(`/api/activities/${id}`),
};

// Routes APIs
export const routesAPI = {
  getRoutes: (params: { page: number; sort: string; sport: string; sortOrder: number }) => {
    // Backend expects: page_number, page_length, sortby, sortorder, sport, without_records, simplified
    const backendParams: Record<string, string | number | boolean> = {
      page_length: 20,
      without_records: false,
      simplified: false,
      sortby:
        params.sort === "date"
          ? "time_last_activity"
          : params.sort === "distance"
            ? "average_distance"
            : params.sort === "pace"
              ? "average_pace"
              : params.sort === "total_ascent"
                ? "average_ascent"
                : params.sort === "technicity"
                  ? "average_technicity"
                  : params.sort,
      sortorder: params.sortOrder,
    };

    // Only add page_number if not first page (backend defaults to 0)
    if (params.page > 1) {
      backendParams.page_number = params.page - 1;
    }

    if (params.sport && params.sport !== "all") {
      backendParams.sport = params.sport;
    }
    return api.get("/api/routes", { params: backendParams });
  },
  getRoute: (id: string) => api.get(`/api/routes/${id}`),
  getAllRoutes: () => {
    // Fetch all routes with high page_length for global map
    const params: Record<string, string | number | boolean> = {
      page_length: -1, // -1 means no limit
      page_number: 0,
      without_records: false, // Need polyline data
      simplified: false, // Need full data for stats
    };
    return api.get("/api/routes", { params });
  },
};

// Courses APIs
export const coursesAPI = {
  getCourses: (params: { page: number; search?: string; isPublic?: boolean }) => {
    const backendParams: Record<string, string | number | boolean> = {
      page_length: 10,
      polyline: true, // Include polyline for map display
      records: true, // Include records to calculate distance
    };

    // Only add page_number if not first page
    if (params.page > 1) {
      backendParams.page_number = params.page - 1;
    }

    if (params.search && params.search.trim().length > 0) {
      backendParams.search = params.search.trim();
    }

    if (params.isPublic !== undefined) {
      backendParams.public = params.isPublic;
    }

    return api.get("/api/courses", { params: backendParams });
  },
  getCourse: (id: string) => api.get(`/api/courses/${id}`),
};

// Stats APIs
export const statsAPI = {
  getStats: (params: { sport?: string; month?: number; year?: number }) => {
    const { sport = "run", month, year } = params;

    // Build URL path like: /api/stats/sport/run/year/2025/month/8
    let url = `/api/stats/sport/${sport}/year/${year}`;
    if (month) {
      url += `/month/${month}`;
    }

    return api.get(url);
  },
};

// Upload APIs
export const uploadAPI = {
  uploadGPX: (file: File, mode: "segmentation" | "analysis") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
    return api.post("/api/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Predictor APIs
export const predictorAPI = {
  // Upload and segment GPX file
  uploadAndSegment: (file: File, courseTitle?: string, coursePublic?: boolean) => {
    const formData = new FormData();
    formData.append("gpx", file);
    if (courseTitle) formData.append("course_title", courseTitle);
    if (coursePublic !== undefined) formData.append("course_public", coursePublic.toString());
    return api.post("/api/routes/gpx/segment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // Predict for activity
  predictActivity: (
    activityId: string,
    targetTime?: number,
    strategy?: string,
    aidStations?: AidStation[]
  ) =>
    api.post(`/api/activities/${activityId}/predict`, {
      ...(targetTime ? { target_time: targetTime } : {}),
      ...(strategy ? { strategy } : {}),
      ...(aidStations && aidStations.length > 0 ? { aid_stations: aidStations } : {}),
    }),
  // Predict for course
  predictCourse: (
    courseId: string,
    targetTime?: number,
    strategy?: string,
    aidStations?: AidStation[]
  ) =>
    api.post("/api/routes/course/predict", {
      course_id: courseId,
      target_time: targetTime,
      ...(strategy ? { strategy } : {}),
      ...(aidStations && aidStations.length > 0 ? { aid_stations: aidStations } : {}),
    }),
  // Segment course
  segmentCourse: (courseId: string) =>
    api.post("/api/routes/course/segment", { course_id: courseId }),
  // Analyze activity by uploading GPX file
  analyzeActivity: (file: File) => {
    const formData = new FormData();
    formData.append("gpx", file);
    return api.post("/api/activities/gpx/analyse", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // Analyze existing activity by ID (calls predict endpoint)
  analyzeActivityById: (activityId: string) => api.post(`/api/activities/${activityId}/predict`),
  // Upload and analyze activity GPX (legacy - kept for compatibility)
  uploadAndAnalyze: (file: File) => {
    const formData = new FormData();
    formData.append("gpx", file);
    return api.post("/api/activities/gpx/analyse", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Races APIs
export const racesAPI = {
  getAllRaces: () => api.get("/api/races"),
  searchRaces: (racename: string) => api.get(`/api/races/${encodeURIComponent(racename)}`),
  getUserEstimations: () => api.get("/api/race/estimations"),
  getUserEstimation: (estimationId: string) => api.get(`/api/race/estimation/${estimationId}`),
  launchRaceEstimator: (race: string, targetPace: number) =>
    api.post("/api/race/estimator", { race, target_pace: targetPace }),
  getRaceEstimation: (race: string, pace: number) => api.get(`/api/race/estimator/${race}/${pace}`),
};

// Usage APIs (admin)
export const usageAPI = {
  getUsage: () => api.get("/api/usage"),
};

// Admin APIs
export const adminAPI = {
  getUsers: (params: { page?: number; pageLength?: number; search?: string } = {}) => {
    const backendParams: Record<string, string | number> = {
      page_number: params.page ?? 0,
      page_length: params.pageLength ?? 50,
    };
    if (params.search && params.search.trim().length > 0) {
      backendParams.search = params.search.trim();
    }
    return api.get("/api/admin/users", { params: backendParams });
  },
  getUserDetail: (userId: string) => api.get(`/api/admin/users/${userId}`),
};

// Notifications APIs
export const notificationsAPI = {
  register: (token: string) => api.post("/api/notifications/register", { token }),
  deregister: (token: string) => api.post("/api/notifications/deregister", { token }),
};

// Device APIs
export const deviceAPI = {
  checkDevice: (deviceCode: string) => api.get(`/api/devices/check/${deviceCode}`),
  confirmDevice: (deviceCode: string) =>
    api.post("/api/devices/confirm", { device_code: deviceCode }),
  getDevices: () => api.get("/api/devices"),
  deleteDevice: (deviceId: string) => api.delete(`/api/devices/${deviceId}`),
};

export default api;
